import type { HelpStep } from "newui/components/ui/Help";

/**
 * Guided tour steps for the Quote Templates editor.
 *
 * One step in the tour ("Copy from Excel") needs to point at an actual row in
 * the spreadsheet, but the editor starts empty. The builder generates a stable
 * demo-row id, uses it in the step's `target` testid, then drives the row in
 * and out of the draft via `onBeforeStep` / `onAfterStep`. This mirrors the
 * `userListSteps` pattern where the tour opens/closes the invite drawer.
 */
export interface QuoteSettingsStepsApi {
	/** Insert a demo row with the supplied id into the draft. */
	addDemoRow: (rowId: number) => void;
	/** Remove the demo row with the supplied id from the draft. */
	removeDemoRow: (rowId: number) => void;
}

/**
 * Stable demo row id used by the tour. Negative so it never collides with a
 * persisted template row id (those are always positive from the API).
 * Hard-coded so consecutive tour runs target the same testid.
 */
const DEMO_ROW_ID = -999_000_001;

/**
 * Guided tour for the CreateQuoteTableModal — walks the user through picking
 * a template, designing columns, naming the table, and the unsaved-changes
 * affordance. Mirrors the structure/pattern of `buildQuoteSettingsSteps` but
 * targets the modal's testids (which differ from the template editor's).
 *
 * The modal renders a preview grid (no editable rows of its own), so the
 * row-centric steps from `buildQuoteSettingsSteps` are omitted here.
 */
export const buildQuoteTableSteps = ({ isEditMode = false }: { isEditMode?: boolean } = {}): HelpStep[] => [
	...(!isEditMode ? [{
		target: '[data-testid="quote-table-template-wrapper"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Pick a template</h3>
				<p>
					Start from a built-in preset (Preliminaries, Trade Package, etc.) or
					one of your saved company templates. Choose <strong>Custom</strong> to
					start from an empty layout.
				</p>
				<p className="pt-2">
					You can also change the template later — but switching after edits
					will replace any unsaved column changes.
				</p>
			</div>
		),
		placement: "bottom" as const,
		disableBeacon: true,
	}] : []),
	{
		target: '[data-testid="spreadsheet-grid-header"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">
					Design your quote table
				</h3>
				<p>This is the column layout your suppliers will see.</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid^="column-header-col-"], [data-testid^="column-header-existing-"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">
					Left-Click a column header
				</h3>
				<p>to edit the name and type</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="column-context-menu"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">
					or right-click for more options
				</h3>
				<p>Insert columns, duplicate, or remove from the context menu.</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
		onBeforeStep: () => {
			const header = document.querySelector(
				'[data-testid^="column-header-col-"], [data-testid^="column-header-existing-"]'
			);
			if (header) {
				const event = new MouseEvent("contextmenu", {
					bubbles: true,
					clientX: header.getBoundingClientRect().left,
					clientY: header.getBoundingClientRect().top,
				});
				header.dispatchEvent(event);
			}
		},
	},
	{
		target: '[data-testid^="column-drag-handle-"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">
					Drag to reorder columns
				</h3>
				<p>Grab the handle and drop the column where you want it.</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="ghost-add-column-cell"]',
		content: (
			<div className="text-sm text-left">
				<h3 className="mb-2 text-base font-semibold">Add a column</h3>
				<p>
					Add as many columns as you need (up to 20). Pick from those you enter (descriptions, unit of measure).
				</p>
				<p className="mt-4 text-left">or provide Supplier Responses:-</p>
				<ul className="list-disc text-left pl-4">
					<li>Value ($)</li>
					<li>Qty (used to calculate Qty * Value</li>
					<li>Saving (a negative value for any savings)</li>
					<li>Text (free text response, like notes or comments)</li>
				</ul>
				<p className="mt-4">You can also add one Total column which will calculate any Qty * Value columns you include. or Value + Value if you have multiple Value columns</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
	},
	{
		target: '[data-testid="quote-table-name-input"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Name your table</h3>
				<p>
					Suppliers see this name on their submission — keep it short and
					descriptive (e.g. &ldquo;Preliminaries&rdquo;,
					&ldquo;Earthworks&rdquo;).
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="quote-table-description-input"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Describe the scope</h3>
				<p>
					Tell suppliers what this table covers — assumptions, inclusions,
					exclusions. This appears alongside the table in their submission.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
];

export const buildQuoteSettingsSteps = ({
	addDemoRow,
	removeDemoRow,
}: QuoteSettingsStepsApi): HelpStep[] => [
		{
			target: '[data-testid="template-name-input"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Name your template</h3>
					<p>
						This is the name you and your team will see when picking a template
						to scaffold a new quote table. Pick something descriptive — e.g.
						&ldquo;Standard Construction Quote&rdquo;.
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-testid="template-quote-table-defaults"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">
						Defaults for the Quote Table
					</h3>
					<p>
						When this template is applied, the new quote table is pre-filled
						with the <strong>Name</strong> and <strong>Description</strong> you
						set here. Suppliers see the description to understand what
						you&apos;re asking for.
					</p>
					<p className="pt-4">These can be edited when the template is used</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-testid="spreadsheet-grid-header"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">
						Design your quote table
					</h3>
					<p>This is the column layout your suppliers will see.</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-testid^="column-header-col-"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">
						Left-Click a column header
					</h3>
					<p>to edit the name and type</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-testid="column-context-menu"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">
						or right-click for more options
					</h3>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
			onBeforeStep: () => {
				// Open the context menu on the first column header so the user can
				// see it in the next step.
				const header = document.querySelector(
					'[data-testid^="column-header-col-"]'
				);
				if (header) {
					const event = new MouseEvent("contextmenu", {
						bubbles: true,
						clientX: header.getBoundingClientRect().left,
						clientY: header.getBoundingClientRect().top,
					});
					header.dispatchEvent(event);
				}
			},
		},
		{
			target: '[data-testid^="column-drag-handle-col-"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">
						You can drag the grip left or right to reorder
					</h3>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-testid="ghost-add-column-cell"]',
			content: (
				<div className="text-sm text-left">
					<h3 className="mb-2 text-base font-semibold">Add a column</h3>
					<p>
						Add as many columns as you need (up to 20). Pick from those you enter (descriptions, unit of measure).
					</p>
					<p className="mt-4 text-left">or provide Supplier Responses:-</p>
					<ul className="list-disc text-left pl-4">
						<li>Value ($)</li>
						<li>Qty (used to calculate Qty * Value</li>
						<li>Saving (a negative value for any savings)</li>
						<li>Text (free text response, like notes or comments)</li>
					</ul>
					<p className="mt-4">You can also add one Total column which will calculate any Qty * Value columns you include. or Value + Value if you have multiple Value columns</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: '[data-testid="template-ghost-add-row"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Add Rows</h3>
					<p>you can add rows that need to be completed by the supplier</p>
					<p>
						(Suppliers can also add their own rows to the table when they
						respond)
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			// First cell of the demo row — the row's name/description input.
			target: `[data-testid^="template-row-"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Copy from Excel</h3>
					<p>
						You can copy from an excel spreadsheet and paste directly into a new
						row — it will then generate all the column and row data (including
						new rows) based on what you have copied
					</p>
					<p className="mt-4">Just click in the first cell on a new row and paste</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
			onBeforeStep: () => addDemoRow(DEMO_ROW_ID),
		},
		{
			target: `[data-testid^="remove-template-row-"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Delete Rows</h3>
					<p>you can delete individual rows</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: `[data-testid="select-row-${DEMO_ROW_ID}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Delete Rows</h3>
					<p>or check multiple rows to delete in bulk</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: `[data-testid="drag-row-${DEMO_ROW_ID}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Reorder Rows</h3>
					<p>
						Change the position of rows by dragging and dropping up/down in the
						grid
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
			onAfterStep: () => removeDemoRow(DEMO_ROW_ID),
		},
		{
			target: '[data-testid="undo-template-btn"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Made a mistake?</h3>
					<p>you can undo up to 5 changes you have made</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
	];
